import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Header, Table } from 'semantic-ui-react';
import { format } from 'date-fns';
import { usePortfolioContext } from '~/hooks';
import { ReactComponent as ChecklistComplete } from '~/assets/checklist-complete.svg';
import InvestorProjectRow from './InvestorProjectRow';
import { sizeKwdc } from '~/utils/calculators/system';
import { offtakeRateUnit } from '~/utils';
import { Button } from '~/components';
import InvestorFeedbackModal from '~/features/projectBidding/investor/modals/BidFeedback';
import { InvestorFeedbackBidStatus } from '~/constants';
import { investorDeclinePortfolio } from '~/api/portfolioBids';

type SortColumn = 'name' | 'pv' | 'storage' | 'bidAmount' | 'submissionDate' | 'bidTotal';
type SortDirection = 'asc' | 'desc';
type SemanticSortDirection = 'ascending' | 'descending';

const InvestorBidding: React.FC = () => {
    const { portfolio, fetchPortfolio } = usePortfolioContext();
    const history = useHistory();
    const [sortColumn, setSortColumn] = useState<SortColumn>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [showDeclineModal, setShowDeclineModal] = useState(false);

    const investorId = portfolio?.projects?.find((p: any) => p?.investorId)?.investorId;
    const allDeclined = (portfolio?.projects || []).length > 0
      && (portfolio?.projects || []).every((p: any) => p?.status === 'DECLINED_TO_BID');
    const hasStorage = (portfolio?.projects || []).some((project: any) => project.storage && project.storage.length > 0);
    const hasPv = (portfolio?.projects || []).some((project: any) => project.projectArrays && project.projectArrays.length > 0);

    // Filter projects that don't have a parentId (top-level projects)
    const projects = (portfolio?.projects || []);

    let formattedDeadline = null;
    if (portfolio.bidDeadline) {
      // Correctly parse only the date portion without applying timezone shifting.
      const dateStr = typeof portfolio.bidDeadline === 'string' ? portfolio.bidDeadline.split('T')[0] : '';
      if (dateStr) {
        // Parse date as if it is "YYYY-MM-DD" local (not UTC), so it won't convert to a previous day.
        const [year, month, day] = dateStr.split('-').map(Number);
        const deadlineDate = new Date(year, month - 1, day); // JS months are 0-based
        if (!isNaN(deadlineDate.getTime())) {
          formattedDeadline = format(deadlineDate, 'MMM d');
        }
      }
    }

    // Check if all projects have bids submitted
    const allProjectsHaveBids = projects.length > 0 && projects.every((project: any) => project.projectBids?.length > 0);

    let headerText: any = 'Submit your non-binding project-level bids ' + (formattedDeadline ? 'by ' + formattedDeadline : '');
    if (allProjectsHaveBids) {
        headerText = <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><ChecklistComplete /> All non-binding bids have been submitted!</div>;
    }

    // Helper functions for sorting
    const calculateTotalPv = (project: any) => {
        return (project.projectArrays?.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0) || 0) / 1000;
    };

    const calculateTotalStorage = (project: any) => {
        return (project.storage?.reduce((sum: number, storage: any) => sum + (storage.wattage || 0), 0) || 0) / 1000;
    };

    const getBidAmountForSorting = (project: any) => {
        const pendingBid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        if (!pendingBid) return 0;

        const projectSizeKwdc = sizeKwdc(project);

        if (project?.pricingType === 'OFFTAKE') {
            return pendingBid.amount;
        }

        // For acquisition/dev fee, normalize to per WDC for consistent comparison
        if (pendingBid.unit === 'TOTAL') {
            return pendingBid.amount / (projectSizeKwdc * 1000);
        }

        return pendingBid.amount;
    };

    const getSubmissionDateForSorting = (project: any) => {
        const pendingBid = project.projectBids?.find((bid: any) => bid.bidStatus === 'PENDING');
        if (pendingBid?.createdAt) {
            return new Date(pendingBid.createdAt);
        }
        return new Date(0);
    };

    // Sort the projects based on current sort column and direction
    const sortedProjects = useMemo(() => {
        const projectsToSort = [...projects];

        return projectsToSort.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'name':
                    aValue = (a?.name || '').toLowerCase();
                    bValue = (b?.name || '').toLowerCase();
                    break;
                case 'pv':
                    aValue = calculateTotalPv(a);
                    bValue = calculateTotalPv(b);
                    break;
                case 'storage':
                    aValue = calculateTotalStorage(a);
                    bValue = calculateTotalStorage(b);
                    break;
                case 'bidAmount':
                    aValue = getBidAmountForSorting(a);
                    bValue = getBidAmountForSorting(b);
                    break;
                case 'submissionDate':
                    aValue = getSubmissionDateForSorting(a);
                    bValue = getSubmissionDateForSorting(b);
                    break;
                default:
                    return 0;
            }

            // Primary sort by the selected column
            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }

            // Secondary sort by project name (ascending) when primary values are equal
            const aName = (a?.name || '').toLowerCase();
            const bName = (b?.name || '').toLowerCase();
            return aName.localeCompare(bName);
        });
    }, [projects, sortColumn, sortDirection]);

    // Handle column header click
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // If clicking the same column, toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a different column, set new column and default to appropriate direction
            setSortColumn(column);
            if (column === 'name') {
                setSortDirection('asc');
            } else {
                setSortDirection('desc');
            }
        }
    };

    // Convert sort direction to Semantic UI format
    const getSemanticSortDirection = (column: SortColumn): SemanticSortDirection | undefined => {
        if (sortColumn !== column) return undefined;
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };

    return (
        <div id="investor-bidding-portfolio">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <Header as="h2">{headerText}</Header>
                {!allDeclined && investorId && (
                    <Button
                        primary
                        outlined
                        onClick={() => setShowDeclineModal(true)}
                    >
                        Decline to bid on entire portfolio
                    </Button>
                )}
            </div>

            {projects.length > 0 ? (
                <div className="bid-table-container">
                    <Table responsive sortable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell
                                    width={3}
                                    sorted={getSemanticSortDirection('name')}
                                    onClick={() => handleSort('name')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Project
                                </Table.HeaderCell>
                                {/* {hasPv ? (
                                    <Table.HeaderCell
                                        width={2}
                                        textAlign="right"
                                        sorted={getSemanticSortDirection('pv')}
                                        onClick={() => handleSort('pv')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        PV
                                    </Table.HeaderCell>
                                ) : null}
                                {hasStorage ? (
                                    <Table.HeaderCell
                                        width={2}
                                        textAlign="right"
                                        sorted={getSemanticSortDirection('storage')}
                                        onClick={() => handleSort('storage')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Storage
                                    </Table.HeaderCell>
                                ) : null} */}

                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('bidAmount')}
                                    onClick={() => handleSort('bidAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid ({portfolio?.projects[0]?.pricingType === 'OFFTAKE' ? `$/${offtakeRateUnit(portfolio?.projects[0]?.contractType)}` : '$/Wdc'})
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('bidTotal')}
                                    onClick={() => handleSort('bidTotal')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid (Total)
                                </Table.HeaderCell>
                                <Table.HeaderCell width={2} textAlign="right">
                                    Actions
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {sortedProjects.map((project: any, index: number) => (
                                <InvestorProjectRow
                                    key={project.id}
                                    project={project}
                                    index={index}
                                    hasStorage={hasStorage}
                                    hasPv={hasPv}
                                    fetchPortfolio={fetchPortfolio}
                                />
                            ))}
                        </Table.Body>
                    </Table>
                </div>
            ) : (
                <p>No projects available for bidding.</p>
            )}

            {showDeclineModal && investorId && (
                <InvestorFeedbackModal
                    bidStatus={InvestorFeedbackBidStatus.BID_DECLINED}
                    closeModal={() => setShowDeclineModal(false)}
                    declineTitle="Decline to bid on portfolio"
                    declineDescription={`We’d like to hear why you’re not interested in bidding on “${portfolio.name}”. Your feedback will be shared with the developer and used to better match you with future opportunities.`}
                    declineMutationFn={(formState: any) => investorDeclinePortfolio(portfolio.id, {
                        investorId,
                        feedbackRating: formState.feedbackRating,
                        feedbackSelection: formState.feedbackSelection,
                        feedbackNotes: formState.feedbackNotes,
                    })}
                    onDeclineSuccess={() => {
                        if (fetchPortfolio) {
                            fetchPortfolio();
                        } else {
                            history.push('/dashboard');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default InvestorBidding;